import {
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
} from "react-native";
import { useTranslation } from "react-i18next";
import type { TimeRange } from "@heraion/shared";

import { formatDayMonthYear } from "@heraion/shared";
import type { UiTheme } from "../theme";
import { useDateLocale } from "../primitives/DateFormatContext";

// Markers the translated sentence carries in place of the two dates, so the
// dates alone take the mono face and the words around them do not.
const FROM = "[[from]]";
const TO = "[[to]]";

/** «From … to …» of a report, whole dates with the year, dates in mono. */
export function ReportPeriodText({
  theme,
  range,
  style,
}: {
  theme: UiTheme;
  range: TimeRange;
  style: StyleProp<TextStyle>;
}) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const date = (ms: number) => formatDayMonthYear(new Date(ms), dateLocale);
  const dateStyle: TextStyle = {
    fontFamily: theme.typography.fonts.mono,
    fontVariant: ["tabular-nums"],
  };
  const parts = t("mobile.progress.report.period", {
    from: FROM,
    to: TO,
    defaultValue: "{{from}} to {{to}}",
  }).split(/(\[\[from\]\]|\[\[to\]\])/);
  return (
    <Text style={style} accessibilityRole="header">
      {parts.map((part, i) =>
        part === FROM || part === TO ? (
          <Text key={i} style={dateStyle}>
            {date(part === FROM ? range.fromMs : range.toMs)}
          </Text>
        ) : (
          part
        ),
      )}
    </Text>
  );
}

/** Which page of the shared series this is, 0-based `index`. */
export type ReportPageNumber = { index: number; total: number };

// PG-64: the head of every shared page: the product, the title, the period,
// and «2 of 5» when the report takes more than one page.
export function ReportPageHeader({
  theme,
  range,
  page,
}: {
  theme: UiTheme;
  range: TimeRange;
  page?: ReportPageNumber;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const pageOf =
    page && page.total > 1
      ? t("common.pageOf", {
          current: page.index + 1,
          total: page.total,
          defaultValue: "{{current}} of {{total}}",
        })
      : null;
  return (
    <View style={styles.head}>
      <View style={styles.topRow}>
        <Text style={styles.brand}>{t("brand.product")}</Text>
        {pageOf ? (
          <Text style={styles.pageOf} accessibilityLabel={pageOf}>
            {pageOf}
          </Text>
        ) : null}
      </View>
      <Text style={styles.title}>
        {t("mobile.progress.report.title", { defaultValue: "Training report" })}
      </Text>
      <ReportPeriodText theme={theme} range={range} style={styles.period} />
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    head: { gap: s.s1 },
    topRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: s.s3,
    },
    brand: { color: p.ink2, fontSize: ty.sizes.lg, fontWeight: "700" },
    pageOf: {
      color: p.ink2,
      fontSize: ty.sizes.base,
      fontWeight: "600",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    title: { color: p.ink, fontSize: ty.sizes.xl, fontWeight: "700" },
    period: { color: p.ink2, fontSize: ty.sizes.base },
  });
}
